<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { computed, watch } from "vue"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

export type ScheduleRange = {
    start: string
    end: string
    label: string
    days: {
        date: string
        weekday: string
        label: string
        shifts: { id: number; name: string; station: string; start: string; end: string; overnight: boolean }[]
    }[]
}

const props = defineProps<{
    range: ScheduleRange
    date: string
    branchId: number
    branchName: string
    indexPath: string
}>()

const form = useForm({ range_start: props.range.start, range_end: props.range.end })
watch(() => props.range, (range) => {
    form.range_start = range.start
    form.range_end = range.end
})
const hasOvernight = computed(() => props.range.days.some((day) => day.shifts.some((shift) => shift.overnight)))

function showRange() {
    form.transform((data) => ({ ...data, date: props.date, branch_id: props.branchId }))
        .get(props.indexPath, { preserveState: true, preserveScroll: true })
}
</script>

<template>
    <section aria-labelledby="schedule-summary-title" class="mb-6 min-w-0">
        <div class="mb-3 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
                <h2 id="schedule-summary-title" class="text-lg font-semibold">Schedule summary</h2>
                <p class="text-xs text-gray-600">Choose up to 31 days. Screenshot the compact schedule below.</p>
            </div>
            <form class="grid min-w-0 grid-cols-2 gap-2 sm:flex sm:items-end" @submit.prevent="showRange">
                <label class="min-w-0 text-xs font-medium">
                    From
                    <Input v-model="form.range_start" type="date" required class="mt-1 min-w-0 max-w-full px-2 sm:w-36" />
                </label>
                <label class="min-w-0 text-xs font-medium">
                    To
                    <Input v-model="form.range_end" type="date" required :min="form.range_start" class="mt-1 min-w-0 max-w-full px-2 sm:w-36" />
                </label>
                <Button type="submit" :disabled="form.processing" class="col-span-2">{{ form.processing ? "Loading…" : "Show schedule" }}</Button>
            </form>
        </div>
        <p v-if="form.errors.range_start || form.errors.range_end" role="alert" class="mb-2 text-sm text-red-700">
            {{ form.errors.range_start || form.errors.range_end }}
        </p>
        <div class="overflow-hidden rounded-xl border border-stone-300 bg-white text-stone-900">
            <div class="border-b border-stone-200 bg-stone-50 px-3 py-2">
                <div class="flex flex-wrap items-baseline justify-between gap-x-3">
                    <h3 class="text-sm font-bold">Antahan Café · Staff schedule</h3>
                    <span class="text-xs font-medium">{{ branchName }}</span>
                </div>
                <p class="mt-0.5 text-xs text-stone-600">{{ range.label }}</p>
            </div>
            <div v-for="day in range.days" :key="day.date" class="grid grid-cols-[3.5rem_minmax(0,1fr)] border-b border-stone-200 last:border-b-0 sm:grid-cols-[5rem_minmax(0,1fr)]">
                <div class="bg-stone-50 px-2 py-2">
                    <p class="text-xs font-bold">{{ day.weekday }}</p>
                    <p class="text-[11px] text-stone-600">{{ day.label }}</p>
                </div>
                <div class="min-w-0 px-2 py-1.5 sm:px-3">
                    <p v-if="!day.shifts.length" class="py-1 text-xs text-stone-500">No staff scheduled</p>
                    <div v-for="shift in day.shifts" :key="shift.id" class="flex items-baseline justify-between gap-2 py-0.5 text-xs leading-4">
                        <div class="min-w-0 break-words">
                            <span class="font-semibold">{{ shift.name }}</span>
                            <span class="ml-1 text-[10px] text-stone-500">{{ shift.station }}</span>
                        </div>
                        <span class="shrink-0 whitespace-nowrap text-[11px] tabular-nums">{{ shift.start }}–{{ shift.end }}<sup v-if="shift.overnight">+1</sup></span>
                    </div>
                </div>
            </div>
            <p v-if="hasOvernight" class="border-t border-stone-200 px-3 py-1 text-[10px] text-stone-500">+1 ends the following day</p>
        </div>
    </section>
</template>
